<div class="min-h-screen bg-base-200/30 pb-16">
  <!-- Top Hero Header Section -->
  <section
    class="bg-base-100 border-b border-base-200 py-8 px-4 sm:px-6 lg:px-8"
  >
    <div class="container mx-auto max-w-7xl">
      <!-- Breadcrumb -->
      <nav
        class="daisy-breadcrumbs text-xs sm:text-sm mb-4"
        aria-label="Breadcrumb"
      >
        <ul>
          <li>
            <a routerLink="/" class="text-base-content/70 hover:text-primary"
              >Home</a
            >
          </li>
          <li>
            <a
              routerLink="/courses"
              class="text-base-content/70 hover:text-primary"
              >Courses</a
            >
          </li>
          <li
            class="font-medium text-base-content truncate max-w-xs sm:max-w-md"
          >
            {{ selectedCourse.title || 'Course Details' }}
          </li>
        </ul>
      </nav>

      <!-- Course Header Details -->
      <div class="max-w-4xl space-y-4">
        <div class="flex flex-wrap items-center gap-2">
          @if (selectedCourse.targetAudience) {
            <span
              class="daisy-badge daisy-badge-primary font-bold text-xs uppercase tracking-wider"
            >
              {{ selectedCourse.targetAudience }}
            </span>
          }
          @if (selectedCourse.subject) {
            <span
              class="daisy-badge daisy-badge-neutral text-white font-bold text-xs uppercase tracking-wider"
            >
              {{ selectedCourse.subject }}
            </span>
          }
          @if (!selectedCourse.targetAudience && !selectedCourse.subject) {
            <span
              class="daisy-badge daisy-badge-primary font-bold text-xs uppercase tracking-wider"
            >
              {{ categoryName }}
            </span>
          }
          <span class="daisy-badge daisy-badge-ghost text-xs font-semibold">
            {{ mediumFormatted }}
          </span>
          @if (isPendingApproval) {
            <span
              class="daisy-badge daisy-badge-warning text-warning-content font-bold text-xs flex items-center gap-1"
            >
              <i class="fa-solid fa-clock text-[10px]"></i> Verification Pending
            </span>
          } @else if (isPaymentPending) {
            <span
              class="daisy-badge daisy-badge-warning text-warning-content font-bold text-xs flex items-center gap-1"
            >
              <i class="fa-solid fa-hourglass-half text-[10px]"></i> Payment Pending
            </span>
          } @else if (isEnrolled) {
            <span
              class="daisy-badge daisy-badge-success text-white font-bold text-xs flex items-center gap-1"
            >
              <i class="fa-solid fa-circle-check text-[10px]"></i> Enrolled
            </span>
          }
        </div>

        <h1
          class="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-base-content tracking-tight leading-tight m-0"
        >
          {{ selectedCourse.title }}
        </h1>

        @if (selectedCourse.details.descriptionSummary) {
          <p
            class="text-base sm:text-lg text-base-content/80 leading-relaxed max-w-3xl m-0"
          >
            {{ selectedCourse.details.descriptionSummary }}
          </p>
        }

        <!-- Quick Meta Chips -->
        <div
          class="flex flex-wrap items-center gap-4 sm:gap-6 pt-3 text-sm text-base-content/70"
        >
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-layer-group text-primary"></i>
            <span
              >{{ totalModules }}
              {{ totalModules === 1 ? 'Module' : 'Modules' }}</span
            >
          </div>
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-circle-play text-primary"></i>
            <span>{{ totalLessons }} Lessons</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-clock text-primary"></i>
            <span>{{ durationLabel }}</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-globe text-primary"></i>
            <span>Self-Paced Learning</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Main Content: 2-Column Responsive Layout -->
  <main class="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-8">
    <div class="flex flex-col lg:flex-row gap-8 items-start">
      <!-- Left Column: Overview, What You'll Learn, Curriculum -->
      <div class="w-full lg:w-2/3 space-y-8">
        <!-- Detailed Description -->
        @if (selectedCourse.details.description) {
          <section
            class="daisy-card daisy-card-bordered bg-base-100 border-base-200 shadow-sm p-6 sm:p-8"
          >
            <h2
              class="text-xl sm:text-2xl font-bold text-base-content mb-4 flex items-center gap-2.5"
            >
              <i class="fa-solid fa-circle-info text-primary text-lg"></i>
              <span>Course Description</span>
            </h2>
            <div
              class="text-sm sm:text-base text-base-content/80 leading-relaxed whitespace-pre-line"
            >
              {{ selectedCourse.details.description }}
            </div>
          </section>
        }

        <!-- What you'll learn / Coverage -->
        @if (
          selectedCourse.details.coverage &&
          selectedCourse.details.coverage.length > 0
        ) {
          <section
            class="daisy-card daisy-card-bordered bg-base-100 border-base-200 shadow-sm p-6 sm:p-8"
          >
            <h2
              class="text-xl sm:text-2xl font-bold text-base-content mb-4 flex items-center gap-2.5"
            >
              <i class="fa-solid fa-circle-check text-success text-lg"></i>
              <span>What You'll Learn</span>
            </h2>
            <ul class="grid grid-cols-1 sm:grid-cols-2 gap-3 m-0 p-0 list-none">
              @for (item of selectedCourse.details.coverage; track item) {
                <li
                  class="flex items-start gap-2.5 text-sm sm:text-base text-base-content/80"
                >
                  <i
                    class="fa-solid fa-check text-success mt-1 shrink-0 text-sm"
                  ></i>
                  <span>{{ item }}</span>
                </li>
              }
            </ul>
          </section>
        }

        <!-- Course Requirements / Prerequisites -->
        @if (
          selectedCourse.details.requirements &&
          selectedCourse.details.requirements.length > 0
        ) {
          <section
            class="daisy-card daisy-card-bordered bg-base-100 border-base-200 shadow-sm p-6 sm:p-8"
          >
            <h2
              class="text-xl sm:text-2xl font-bold text-base-content mb-4 flex items-center gap-2.5"
            >
              <i class="fa-solid fa-list-check text-primary text-lg"></i>
              <span>Prerequisites & Requirements</span>
            </h2>
            <ul class="space-y-2 m-0 p-0 list-none">
              @for (req of selectedCourse.details.requirements; track req) {
                <li
                  class="flex items-start gap-2.5 text-sm sm:text-base text-base-content/80"
                >
                  <i
                    class="fa-solid fa-circle-dot text-primary/70 mt-1 shrink-0 text-xs"
                  ></i>
                  <span>{{ req }}</span>
                </li>
              }
            </ul>
          </section>
        }

        <!-- Curriculum / Syllabus Accordion -->
        <section
          class="daisy-card daisy-card-bordered bg-base-100 border-base-200 shadow-sm p-6 sm:p-8"
        >
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6"
          >
            <div>
              <h2
                class="text-xl sm:text-2xl font-bold text-base-content flex items-center gap-2.5 m-0"
              >
                <i class="fa-solid fa-book-open text-primary text-lg"></i>
                <span>Course Curriculum</span>
              </h2>
              <p class="text-xs sm:text-sm text-base-content/60 mt-1 m-0">
                {{ totalModules }}
                {{ totalModules === 1 ? 'Module' : 'Modules' }} •
                {{ totalLessons }} Lessons
              </p>
            </div>
          </div>

          @if (selectedCourse.contents && selectedCourse.contents.length > 0) {
            <div class="space-y-3">
              @for (
                module of selectedCourse.contents;
                track module.topic;
                let mIdx = $index
              ) {
                <div
                  class="daisy-collapse daisy-collapse-arrow bg-base-200/50 border border-base-300 rounded-xl"
                >
                  <input
                    type="checkbox"
                    [checked]="activeModuleIndex === mIdx"
                    (change)="toggleModule(mIdx)"
                  />
                  <div
                    class="daisy-collapse-title flex items-center justify-between gap-4 font-semibold text-base sm:text-lg text-base-content pr-12"
                  >
                    <div class="flex items-center gap-3">
                      <span
                        class="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0"
                      >
                        {{ mIdx + 1 }}
                      </span>
                      <span>{{ module.topic }}</span>
                    </div>
                    <span
                      class="text-xs text-base-content/50 font-normal shrink-0"
                    >
                      {{ module.resources ? module.resources.length : 0 }}
                      {{ module.resources.length === 1 ? 'lesson' : 'lessons' }}
                    </span>
                  </div>

                  <div class="daisy-collapse-content pt-0 pb-4">
                    @if (module.resources && module.resources.length > 0) {
                      <div
                        class="divide-y divide-base-300 border-t border-base-300 mt-2"
                      >
                        @for (
                          resource of module.resources;
                          track resource.title;
                          let rIdx = $index
                        ) {
                          <div
                            class="flex items-center justify-between py-2.5 px-2 hover:bg-base-200/60 rounded-lg transition-colors"
                            [class.cursor-pointer]="resource.isFreePreview || isEnrolled"
                            (click)="resource.isFreePreview ? openPreview(resource) : (isEnrolled ? goToLearning() : null)"
                          >
                            <div class="flex items-center gap-3 min-w-0">
                              @if (getResourceType(resource) === 'document') {
                                <i
                                  class="fa-solid fa-file-lines text-primary/70 text-sm shrink-0"
                                ></i>
                              } @else if (getResourceType(resource) === 'link') {
                                <i
                                  class="fa-solid fa-arrow-up-right-from-square text-primary/70 text-sm shrink-0"
                                ></i>
                              } @else {
                                <i
                                  class="fa-solid fa-circle-play text-primary/70 text-sm shrink-0"
                                ></i>
                              }
                              <span
                                class="text-sm font-medium text-base-content truncate"
                                >{{ resource.title }}</span
                              >
                            </div>
                            <div class="flex items-center gap-2 shrink-0">
                              @if (resource.isFreePreview) {
                                <button
                                  type="button"
                                  (click)="$event.stopPropagation(); openPreview(resource)"
                                  class="daisy-btn daisy-btn-accent daisy-btn-xs font-bold gap-1 rounded-full shadow-sm hover:scale-105 transition-transform"
                                >
                                  <i class="fa-solid fa-circle-play text-xs"></i>
                                  Preview
                                </button>
                              }
                              @if (isEnrolled) {
                                <span
                                  class="text-xs text-success font-semibold flex items-center gap-1"
                                >
                                  <i class="fa-solid fa-check text-[10px]"></i>
                                  Available
                                </span>
                              } @else if (!resource.isFreePreview) {
                                <i
                                  class="fa-solid fa-lock text-base-content/40 text-xs"
                                  title="Enroll to unlock"
                                ></i>
                              }
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <p class="text-xs text-base-content/60 italic mt-2 px-2">
                        Lessons and materials will be published soon.
                      </p>
                    }
                  </div>
                </div>
              }
            </div>
          } @else {
            <p class="text-sm text-base-content/70 italic">
              Curriculum details will be announced soon.
            </p>
          }
        </section>
      </div>

      <!-- Right Column: Sticky Checkout Sidebar -->
      <div class="w-full lg:w-1/3 shrink-0">
        <div
          class="sticky top-20 daisy-card daisy-card-bordered bg-base-100 border-base-300 shadow-md overflow-hidden"
        >
          <!-- Banner Preview -->
          <figure
            class="relative aspect-video w-full m-0 bg-base-200 overflow-hidden group"
          >
            @if (bannerImageUrl) {
              <img
                [src]="bannerImageUrl"
                [alt]="selectedCourse.title"
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            } @else {
              <div
                class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-base-content/50 bg-base-200"
              >
                <i class="fa-solid fa-graduation-cap text-4xl"></i>
              </div>
            }
            @if (hasFreePreview) {
              <div
                class="absolute inset-0 bg-black/40 hover:bg-black/50 flex flex-col items-center justify-center gap-2 transition-all cursor-pointer z-10"
                (click)="openPreview(previewLessons[0])"
              >
                <button
                  type="button"
                  class="w-14 h-14 rounded-full bg-primary text-primary-content hover:scale-110 flex items-center justify-center text-xl transition-transform shadow-xl pointer-events-none"
                  title="Preview Course"
                >
                  <i class="fa-solid fa-play ml-1"></i>
                </button>
                <span
                  class="text-xs font-semibold text-white bg-black/60 px-3 py-1 rounded-full backdrop-blur-sm pointer-events-none"
                >
                  Preview Course
                </span>
              </div>
            }
          </figure>

          <!-- Card Content -->
          <div class="daisy-card-body p-6 space-y-5">
            <!-- Price Block -->
            <div>
              <div class="flex items-baseline gap-2">
                <span
                  class="text-3xl font-extrabold tracking-tight"
                  [class.text-success]="price === 0"
                  [class.text-base-content]="price > 0"
                >
                  {{ priceFormatted }}
                </span>
                <span
                  class="text-xs text-base-content/60 uppercase font-semibold"
                >
                  {{
                    isEnrolled
                      ? 'Enrolled'
                      : price === 0
                        ? 'Free Course'
                        : 'One-Time Purchase'
                  }}
                </span>
              </div>
              @if (price === 0) {
                <div
                  class="daisy-alert daisy-alert-success mt-2 py-2 px-3 text-sm shadow-sm"
                >
                  <i class="fa-solid fa-circle-check"></i>
                  <span>This course is completely free!</span>
                </div>
              } @else {
                <p class="text-xs text-base-content/60 mt-1">
                  Full lifetime access to all current and future curriculum
                  updates.
                </p>
              }
            </div>

            <!-- Action Buttons -->
            <div class="space-y-2.5">
              @if (isOverdue) {
                <div class="daisy-alert daisy-alert-error py-2 px-3 text-xs shadow-sm text-white">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                  <span>Installment payment is overdue. Please complete payment to resume access.</span>
                </div>
                <button
                  type="button"
                  class="daisy-btn daisy-btn-error w-full gap-2 shadow-md text-white font-semibold"
                  (click)="goToOrderPayment()"
                >
                  <i class="fa-solid fa-credit-card"></i>
                  <span>Pay Overdue Installment</span>
                </button>
              } @else if (isEnrolled) {
                @if (isPendingApproval) {
                  <div class="daisy-alert daisy-alert-info py-2 px-3 text-xs shadow-sm">
                    <i class="fa-solid fa-clock"></i>
                    <span>Payment submitted and awaiting verification. You can continue learning.</span>
                  </div>
                } @else if (isPaymentPending) {
                  <div class="daisy-alert daisy-alert-warning py-2 px-3 text-xs shadow-sm">
                    <i class="fa-solid fa-clock"></i>
                    <span>Installment is due. You can continue learning during your grace period.</span>
                  </div>
                }
                <button
                  type="button"
                  class="daisy-btn daisy-btn-primary w-full gap-2 shadow-md"
                  (click)="goToLearning()"
                >
                  <i class="fa-solid fa-circle-play"></i>
                  <span>Continue Learning</span>
                </button>
              } @else if (isPendingApproval) {
                <button
                  type="button"
                  class="daisy-btn daisy-btn-warning daisy-btn-outline w-full gap-2 shadow-md"
                  (click)="goToOrders()"
                >
                  <i class="fa-solid fa-clock"></i>
                  <span>Pending Approval — View Orders</span>
                </button>
              } @else if (isPaymentPending) {
                <button
                  type="button"
                  class="daisy-btn daisy-btn-warning w-full gap-2 shadow-md"
                  (click)="goToOrderPayment()"
                >
                  <i class="fa-solid fa-credit-card"></i>
                  <span>Complete Payment</span>
                </button>
              } @else if (price === 0) {
                <button
                  type="button"
                  class="daisy-btn daisy-btn-success w-full gap-2 shadow-md"
                  (click)="enrollNow()"
                >
                  <i class="fa-solid fa-rocket"></i>
                  <span>Start Learning — Free</span>
                </button>
              } @else {
                <button
                  type="button"
                  class="daisy-btn daisy-btn-primary w-full gap-2 shadow-md"
                  (click)="enrollNow()"
                >
                  <i class="fa-solid fa-bolt"></i>
                  <span>Enroll Now</span>
                </button>
              }
            </div>

            <!-- Course Inclusions & Highlights -->
            <div
              class="pt-4 border-t border-base-200 space-y-3 text-xs text-base-content/80"
            >
              <p class="font-bold text-base-content text-sm mb-2">
                This course includes:
              </p>
              <div class="flex items-center gap-3">
                <i
                  class="fa-solid fa-circle-play text-primary w-4 text-center"
                ></i>
                <span>{{ totalLessons }} on-demand video lectures</span>
              </div>
              <div class="flex items-center gap-3">
                <i class="fa-solid fa-clock text-primary w-4 text-center"></i>
                <span>{{ durationLabel }} learning duration</span>
              </div>
              <div class="flex items-center gap-3">
                <i
                  class="fa-solid fa-file-arrow-down text-primary w-4 text-center"
                ></i>
                <span>Downloadable learning resources</span>
              </div>
              <div class="flex items-center gap-3">
                <i
                  class="fa-solid fa-mobile-screen-button text-primary w-4 text-center"
                ></i>
                <span>Access on mobile, tablet, and desktop</span>
              </div>
              <div class="flex items-center gap-3">
                <i
                  class="fa-solid fa-certificate text-primary w-4 text-center"
                ></i>
                <span>Certificate of completion</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>

  <!-- Free Preview Video Modal -->
  @if (isPreviewModalOpen) {
    <div class="daisy-modal daisy-modal-open z-50">
      <div class="daisy-modal-box max-w-4xl p-0 overflow-hidden bg-neutral-900 border border-neutral-700 shadow-2xl">
        <!-- Modal Header -->
        <div class="flex items-center justify-between px-6 py-4 bg-neutral-800 border-b border-neutral-700">
          <div class="flex items-center gap-3 min-w-0">
            <span class="daisy-badge daisy-badge-accent daisy-badge-sm font-bold shrink-0">Free Preview</span>
            <h3 class="font-bold text-white text-base truncate">
              {{ activePreviewResource?.title || 'Lesson Preview' }}
            </h3>
          </div>
          <button
            type="button"
            (click)="closePreview()"
            class="daisy-btn daisy-btn-sm daisy-btn-circle daisy-btn-ghost text-white hover:bg-white/10"
          >
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <!-- Player Container -->
        <div class="w-full aspect-video bg-black flex items-center justify-center relative">
          @if (previewVideoUrl) {
            <black-video-player [resourceId]="previewVideoUrl" [autoplay]="true"></black-video-player>
          } @else {
            <div class="flex flex-col items-center gap-2 text-white/60 p-6 text-center">
              <i class="fa-solid fa-triangle-exclamation text-3xl text-warning"></i>
              <p class="text-sm">Preview content is currently unavailable.</p>
            </div>
          }
        </div>

        <!-- Modal Footer -->
        <div class="flex items-center justify-between px-6 py-3 bg-neutral-800 border-t border-neutral-700">
          <span class="text-xs text-neutral-400">
            Enjoying this lesson? Enroll to get full access to all course materials.
          </span>
          <button
            type="button"
            (click)="closePreview(); enrollNow()"
            class="daisy-btn daisy-btn-primary daisy-btn-sm font-bold"
          >
            Enroll Now
          </button>
        </div>
      </div>
      <div class="daisy-modal-backdrop bg-black/70" (click)="closePreview()"></div>
    </div>
  }
</div>
